import type * as React from 'react';

import { table } from '@gitbook/doc-core';

import { isModShiftSlash } from '../../common';
import { useTablePlugin } from '../plugin';
import { definitionSpecs } from './definitions';
import type { RecordValueProps, TableRecordValueProps } from './types';

/**
 * Display a column value in a record.
 * Note: in process of being split up into dedicated components for Tables and Cards. Table cells already changed to use TableRecordValue.tsx.
 */
export function RecordValue(props: RecordValueProps | TableRecordValueProps): React.ReactElement {
    const {
        record,
        columnDefinition,
        readOnly,
        displayMode = 'cell',
        editor,
        nodeKey,
        selected,
        view,
        definitionColorKind,
        diff,
        onPressRowOptions,
    } = props;

    const { ValueComponent } = definitionSpecs[props.columnDefinition.type];
    const tablePlugin = useTablePlugin();
    const onValueChange = async (value) => {
        editor.change((change) => {
            table.setRecordValue(change, nodeKey, record.key, columnDefinition.id, value);
        });
        // Maintain selection after inserting value, otherwise the selection gets lost when inserting values through palette's and dialogs (e.g. in Files, Users, Select).
        editor.change((change) => {
            table.moveTableSelection(change, nodeKey, {
                recordId: record.key,
                definitionId: columnDefinition.id,
            });
        });
    };

    const onSelectionChange = (focused: boolean) => {
        if (focused) {
            editor.change((change) => {
                table.moveTableSelection(change, nodeKey, {
                    recordId: record.key,
                    definitionId: columnDefinition.id,
                });
            });
        }
    };

    // Handle keydown in non-Slate node, and have them also trigger the table plugin's onKeyDown handler. Slate nodes (like a Text cell) are handled through the plugins, but non-Slate nodes like most other cells should also be evaluated.
    const handleNonSlateKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
        // Handle opening the row options palette shortcut
        if (isModShiftSlash(event)) {
            event.preventDefault();
            onPressRowOptions && selected && onPressRowOptions();
            return;
        }

        event.preventDefault();

        // Execute the same onKeyDown handler as the table plugin, only on non-Slate nodes
        if (tablePlugin.onKeyDown) {
            // Create a change object to pass to the onKeyDown handler
            const change = editor.value.change();
            tablePlugin.onKeyDown(event as unknown as KeyboardEvent, change, editor);

            editor.onChange(change);
        }
    };

    const diffView = diff?.recordValues.get(record.key)?.get(columnDefinition.id);
    const PrevValueComponent =
        typeof diffView?.before !== 'undefined'
            ? definitionSpecs[diffView.before.definition.type].ValueComponent
            : null;

    const innerContent = diffView ? (
        <div className="flex w-fit flex-1 flex-col gap-1">
            {PrevValueComponent && typeof diffView.before !== 'undefined' ? (
                <div className="bg-danger-standout p-1">
                    <PrevValueComponent
                        editor={editor}
                        nodeKey={nodeKey}
                        readOnly={readOnly}
                        displayMode={displayMode}
                        selected={selected}
                        value={diffView.before.value}
                        definitionColorKind={definitionColorKind}
                        definition={diffView.before.definition}
                        onValueChange={onValueChange}
                        onSelectionChange={onSelectionChange}
                        view={view}
                        onKeyDown={handleNonSlateKeyDown}
                    />
                </div>
            ) : null}
            <div className="bg-success-standout p-1">
                <ValueComponent
                    editor={editor}
                    nodeKey={nodeKey}
                    readOnly={readOnly}
                    displayMode={displayMode}
                    selected={selected}
                    value={diffView.after}
                    definitionColorKind={definitionColorKind}
                    definition={columnDefinition}
                    onValueChange={onValueChange}
                    onSelectionChange={onSelectionChange}
                    view={view}
                    onKeyDown={handleNonSlateKeyDown}
                />
            </div>
        </div>
    ) : (
        <ValueComponent
            editor={editor}
            nodeKey={nodeKey}
            readOnly={readOnly}
            displayMode={displayMode}
            selected={selected}
            value={record.value.values[columnDefinition.id]}
            definitionColorKind={definitionColorKind}
            definition={columnDefinition}
            onValueChange={onValueChange}
            onSelectionChange={onSelectionChange}
            view={view}
            onKeyDown={handleNonSlateKeyDown}
        />
    );

    if (columnDefinition.type === 'text') {
        return innerContent;
    }

    return (
        <div
            className="flex flex-1 items-center"
            onClick={(event) => {
                // We prevent the event from being handled by the editor and moving focus out of the input
                event.stopPropagation();
                onSelectionChange(true);
            }}
            tabIndex={-1}
        >
            {innerContent}
        </div>
    );
}
